import { createContext, useContext, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useInitiative } from '../hooks/useInitiative';
import { useChatContext } from './ChatContext';
import { useSessionContext } from './SessionContext';
import type { Token } from '../types';

type UseInitiativeReturn = ReturnType<typeof useInitiative>;

export interface InitiativeContextValue {
  initiative: UseInitiativeReturn;
}

const InitiativeContext = createContext<InitiativeContextValue | null>(null);

export function InitiativeProvider({ children }: { children: ReactNode }) {
  const { chat } = useChatContext();
  const { mp } = useSessionContext();
  const initiative = useInitiative(chat);

  // Keep a stable ref to the latest initiative to avoid re-creating callbacks
  const initiativeRef = useRef(initiative);
  initiativeRef.current = initiative;

  const mpRef = useRef(mp);
  mpRef.current = mp;

  // Multiplayer-aware initiative wrappers — stable callbacks via refs
  const mpRollAll = useCallback(
    (toks: Token[]) => {
      const newEntries = initiativeRef.current.rollAll(toks);
      if (mpRef.current.session) {
        mpRef.current.send({
          type: 'initiative:update',
          entries: newEntries,
          currentIndex: 0,
          round: 1,
        } as any);
      }
      return newEntries;
    },
    [],
  );

  const mpNext = useCallback(() => {
    initiativeRef.current.next();
    if (mpRef.current.session) mpRef.current.send({ type: 'initiative:next' } as any);
  }, []);

  const mpReset = useCallback(() => {
    initiativeRef.current.reset();
    if (mpRef.current.session) mpRef.current.send({ type: 'initiative:reset' } as any);
  }, []);

  // Stabilize the context value: only create a new object when actual data changes
  const { entries, currentIndex, round, setEntries, setCurrentIndex, setRound } = initiative;
  const isMultiplayer = !!mp.session;

  const mpInitiative = useMemo(
    (): UseInitiativeReturn =>
      isMultiplayer
        ? { entries, currentIndex, round, setEntries, setCurrentIndex, setRound, rollAll: mpRollAll, next: mpNext, reset: mpReset }
        : { entries, currentIndex, round, setEntries, setCurrentIndex, setRound, rollAll: initiative.rollAll, next: initiative.next, reset: initiative.reset },
    [isMultiplayer, entries, currentIndex, round, setEntries, setCurrentIndex, setRound, mpRollAll, mpNext, mpReset, initiative.rollAll, initiative.next, initiative.reset],
  );

  const value = useMemo(() => ({ initiative: mpInitiative }), [mpInitiative]);

  return (
    <InitiativeContext.Provider value={value}>
      {children}
    </InitiativeContext.Provider>
  );
}

export function useInitiativeContext() {
  const ctx = useContext(InitiativeContext);
  if (!ctx) throw new Error('useInitiativeContext must be used within InitiativeProvider');
  return ctx;
}
